import React from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { AlertTriangle, X } from "lucide-react";
import { getAvatarColors } from "../common/ProfileDetailPanel";

const DuplicateProfileDialog = ({ open, onClose, onImport, existingProfile }) => {
  if (!existingProfile) return null;

  const fullName = [existingProfile.first_name, existingProfile.last_name].filter(Boolean).join(" ");
  const colors = getAvatarColors(existingProfile.id);
  const pipelines = existingProfile.pipelines || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          border: "1px solid hsl(140 8% 88%)",
          maxWidth: 420,
          width: "100%",
          p: 0,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid hsl(140 8% 88%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AlertTriangle size={16} style={{ color: "hsl(38 92% 50%)" }} />
          <Typography
            sx={{
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 30% 12%)",
            }}
          >
            Duplicate profile found
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            p: 0.5,
            borderRadius: "6px",
            color: "hsl(150 10% 46%)",
            "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
          }}
        >
          <X size={14} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            color: "hsl(150 10% 46%)",
            mb: 2,
          }}
        >
          A profile with the same email or phone already exists. Do you want to import their information into this pipeline?
        </Typography>

        {/* Existing profile card */}
        <Box
          sx={{
            borderRadius: "10px",
            border: "1px solid hsl(140 8% 88%)",
            backgroundColor: "hsl(140 10% 95%)",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 13,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                bgcolor: colors.bg,
                color: colors.text,
              }}
            >
              {(existingProfile.first_name || "")[0]}
              {(existingProfile.last_name || "")[0]}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                }}
                noWrap
              >
                {fullName}
              </Typography>
              {existingProfile.desired_title && (
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    color: "hsl(150 10% 46%)",
                  }}
                  noWrap
                >
                  {existingProfile.desired_title}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {existingProfile.email && (
              <Box>
                <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 0.25 }}>
                  Email
                </Typography>
                <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }} noWrap>
                  {existingProfile.email}
                </Typography>
              </Box>
            )}
            {existingProfile.phone && (
              <Box>
                <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 0.25 }}>
                  Phone
                </Typography>
                <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {existingProfile.phone}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Owner & pipelines */}
          {(existingProfile.owner || pipelines.length > 0) && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid hsl(140 8% 88%)" }}>
              {existingProfile.owner && (
                <Box sx={{ mb: pipelines.length > 0 ? 1 : 0 }}>
                  <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 0.25 }}>
                    Added by
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    {existingProfile.owner}
                  </Typography>
                </Box>
              )}
              {pipelines.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 0.25 }}>
                    Already in {pipelines.length === 1 ? "pipeline" : "pipelines"}
                  </Typography>
                  {pipelines.map((p) => (
                    <Typography
                      key={p.id}
                      sx={{
                        fontSize: 13,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                      }}
                    >
                      {p.title}
                      {p.owner && (
                        <Box component="span" sx={{ color: "hsl(150 10% 46%)" }}>
                          {" "}— {p.owner}
                        </Box>
                      )}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid hsl(140 8% 88%)",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disableElevation
          sx={{
            flex: 1,
            height: 38,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            border: "1px solid hsl(140 8% 88%)",
            color: "hsl(150 30% 12%)",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onImport}
          disableElevation
          variant="contained"
          sx={{
            flex: 1,
            height: 38,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)" },
          }}
        >
          Import profile
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateProfileDialog;
